/** Devis — aligné sur `documents.php`. */
import type { GcCharge } from "../../payments/gocardless";
import { inputToCents, centsToInput, normalizeDecimal, parseQty } from "../catalog/types";
import type { Product } from "../catalog/types";
import { formatQty } from "../../formats/display";
import { opKindOf, type OpKind } from "../../legal/opNature";

export type { TaxRate } from "../catalog/types";

export type QuoteLine = {
  id?: number;
  product_id: number | null;
  label: string;
  description: string | null;
  product_code?: string | null;
  quantity: string | number;
  unit_price_ht_cents: number;
  discount_bp?: number;
  discount_cents?: number;
  tax_rate_id: number | null;
  op_kind?: OpKind | string | null;
  line_ht_cents?: number;
  line_tax_cents?: number;
};

export type QuoteSignature = {
  signer_name: string;
  signed_at: string;
  proof_hash: string;
  invoice_id?: number | null;
};

export type DocKind = "quote" | "invoice" | "credit_note";

export type Quote = {
  id: number;
  company_id: number;
  client_id: number;
  client_name?: string | null;
  doc_type: DocKind;
  status: "draft" | "validated" | string;
  number: string | null;
  issue_date: string | null;
  operation_date?: string | null;
  due_date: string | null;
  valid_until?: string | null;
  total_ht_cents: number;
  total_tax_cents: number;
  total_ttc_cents: number;
  discount_bp?: number;
  discount_cents?: number;
  notes?: string | null;
  terms_html?: string | null;
  footer_html?: string | null;
  immutable?: boolean;
  created_at?: string;
  validated_at?: string | null;
  signed_at?: string | null;
  viewed_at?: string | null;
  signer_name?: string | null;
  proof_hash?: string | null;
  content_hash?: string | null;
  archive_rel?: string | null;
  archived?: boolean;
  shelved_at?: string | null;
  converted_invoice_id?: number | null;
  credit_note_id?: number | null;
  credited?: boolean;
  related_document_id?: number | null;
  related_number?: string | null;
  paid_cents?: number;
  remaining_cents?: number;
  paid?: boolean;
  overdue?: boolean;
  gc_charge?: GcCharge | null;
  signature?: QuoteSignature | null;
  refused_at?: string | null;
  refusal?: { refuser_name?: string; reason?: string | null; refused_at?: string } | null;
  lines?: QuoteLine[];
};

export type LineDraft = {
  key: string;
  product_id: number | null;
  label: string;
  description: string;
  product_code: string;
  quantity: string;
  price: string;
  discount: string;
  discount_kind: "pct" | "eur";
  tax_rate_id: number | null;
  op_kind: OpKind;
};

export type QuoteDraft = {
  id?: number;
  client_id: number;
  issue_date: string;
  operation_date: string;
  due_date: string;
  notes: string;
  terms_html: string;
  footer_html: string;
  discount: string;
  discount_kind: "pct" | "eur";
  lines: LineDraft[];
  status: string;
  number: string | null;
  immutable: boolean;
  signature: QuoteSignature | null;
  refusal: { refuser_name: string; reason: string | null; refused_at: string } | null;
  converted_invoice_id: number | null;
  credit_note_id?: number | null;
  credited?: boolean;
  related_number?: string | null;
  viewed_at?: string | null;
  paid_cents?: number;
  remaining_cents?: number;
  paid?: boolean;
  total_ttc_cents?: number;
  archived?: boolean;
};

export type PaymentTerm = {
  id: number;
  label: string;
  days: number;
  end_of_month?: boolean | number;
  is_default?: boolean | number;
};

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + days);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function emptyLine(taxId: number | null, decimalComma: boolean): LineDraft {
  return {
    key: `n-${Math.random().toString(36).slice(2, 9)}`,
    product_id: null,
    label: "",
    description: "",
    product_code: "",
    quantity: "1",
    price: centsToInput(0, decimalComma),
    discount: "",
    discount_kind: "pct",
    tax_rate_id: taxId,
    op_kind: "service",
  };
}

export function emptyDraft(
  validDays: number,
  taxId: number | null,
  decimalComma: boolean,
  extras?: { terms_html?: string; footer_html?: string },
): QuoteDraft {
  const issue = todayIso();
  return {
    client_id: 0,
    issue_date: issue,
    operation_date: issue,
    due_date: addDays(issue, validDays >= 1 ? validDays : 30),
    notes: "",
    terms_html: extras?.terms_html || "",
    footer_html: extras?.footer_html || "",
    discount: "",
    discount_kind: "pct",
    lines: [emptyLine(taxId, decimalComma)],
    status: "draft",
    number: null,
    immutable: false,
    signature: null,
    refusal: null,
    converted_invoice_id: null,
  };
}

export function lineFromProduct(p: Product, decimalComma: boolean): LineDraft {
  return {
    key: `p-${p.id}-${Math.random().toString(36).slice(2, 7)}`,
    product_id: p.id,
    label: p.label,
    description: p.description || "",
    product_code: p.product_code || "",
    quantity: String(p.default_qty || 1),
    price: centsToInput(p.unit_price_ht_cents, decimalComma),
    discount: "",
    discount_kind: "pct",
    tax_rate_id: p.tax_rate_id,
    op_kind: opKindOf(p.op_kind),
  };
}

export function quoteToDraft(
  q: Quote,
  decimalComma: boolean,
  fallbackTax: number | null,
  extras?: { terms_html?: string; footer_html?: string },
): QuoteDraft {
  const lines = (q.lines || []).map((l) => ({
    key: `l-${l.id ?? Math.random().toString(36).slice(2, 7)}`,
    product_id: l.product_id ? Number(l.product_id) : null,
    label: l.label || "",
    description: l.description || "",
    product_code: l.product_code || "",
    quantity: formatQty(l.quantity, decimalComma),
    price: centsToInput(Number(l.unit_price_ht_cents) || 0, decimalComma),
    ...storedDiscount(Number(l.discount_bp) || 0, Number(l.discount_cents) || 0, decimalComma),
    tax_rate_id: l.tax_rate_id === null || l.tax_rate_id === undefined ? fallbackTax : Number(l.tax_rate_id),
    op_kind: opKindOf(l.op_kind),
  }));
  return {
    id: q.id,
    client_id: Number(q.client_id) || 0,
    issue_date: q.issue_date || todayIso(),
    operation_date: q.operation_date || q.issue_date || todayIso(),
    due_date: (q.doc_type === "quote" ? q.valid_until || q.due_date : q.due_date) || "",
    notes: plainNotes(q.notes),
    terms_html: q.terms_html || extras?.terms_html || "",
    footer_html: q.footer_html || extras?.footer_html || "",
    ...storedDiscount(Number(q.discount_bp) || 0, Number(q.discount_cents) || 0, decimalComma),
    lines: lines.length ? lines : [emptyLine(fallbackTax, decimalComma)],
    status: q.status,
    number: q.number,
    immutable: q.immutable === true || q.status === "validated",
    signature: q.signature ?? (q.signed_at ? { signer_name: q.signer_name || "", signed_at: q.signed_at, proof_hash: q.proof_hash || "" } : null),
    refusal: q.refusal?.refused_at
      ? {
          refuser_name: q.refusal.refuser_name || "",
          reason: q.refusal.reason || null,
          refused_at: q.refusal.refused_at,
        }
      : q.refused_at
        ? { refuser_name: "", reason: null, refused_at: q.refused_at }
        : null,
    converted_invoice_id: q.converted_invoice_id ? Number(q.converted_invoice_id) : null,
    credit_note_id: q.credit_note_id ? Number(q.credit_note_id) : null,
    credited: q.credited === true,
    related_number: q.related_number || null,
    viewed_at: q.viewed_at || null,
    paid_cents: Number(q.paid_cents) || 0,
    remaining_cents: q.remaining_cents,
    paid: q.paid === true,
    total_ttc_cents: Number(q.total_ttc_cents) || 0,
    archived: q.archived === true,
  };
}

export function lineKeptWithoutLabel(line: LineDraft, decimalComma: boolean): boolean {
  if (line.label.trim() !== "") return false;
  const cents = inputToCents(line.price, decimalComma);
  const desc = line.description.replace(/<[^>]+>/g, " ").replace(/&nbsp;|\u00a0/g, " ").trim();
  return Boolean(line.product_id) || line.product_code.trim() !== "" || desc !== "" || (cents ?? 0) !== 0;
}

export function draftToPayload(
  draft: QuoteDraft,
  companyId: number,
  decimalComma: boolean,
  docType: DocKind = "quote",
) {
  return {
    company_id: companyId,
    doc_type: docType,
    client_id: draft.client_id,
    issue_date: draft.issue_date,
    operation_date: draft.operation_date || draft.issue_date,
    due_date: draft.due_date,
    valid_until: docType === "quote" ? draft.due_date : null,
    notes: plainNotes(draft.notes),
    terms_html: draft.terms_html,
    footer_html: draft.footer_html,
    discount_bp: draft.discount_kind === "pct" ? inputToBp(draft.discount, decimalComma) : 0,
    discount_cents: draft.discount_kind === "eur" ? (inputToCents(draft.discount, decimalComma) ?? 0) : 0,
    lines: draft.lines
      .map((l) => {
        const cents = inputToCents(l.price, decimalComma);
        const qty = parseQty(l.quantity);
        return {
          product_id: l.product_id,
          label: l.label.trim(),
          description: l.description.trim(),
          product_code: l.product_code.trim(),
          quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
          unit_price_ht_cents: cents ?? 0,
          discount_bp: l.discount_kind === "pct" ? inputToBp(l.discount, decimalComma) : 0,
          discount_cents: l.discount_kind === "eur" ? (inputToCents(l.discount, decimalComma) ?? 0) : 0,
          tax_rate_id: l.tax_rate_id,
          op_kind: opKindOf(l.op_kind),
        };
      })
      .filter((l) => {
        if (l.label !== "") return true;
        if (l.product_id) return true;
        if (l.product_code !== "") return true;
        const desc = l.description.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").trim();
        if (desc !== "") return true;
        return l.unit_price_ht_cents !== 0;
      }),
  };
}

/** 10 % → 1000 (points de base). */
function plainNotes(raw: string | null | undefined): string {
  const t = (raw || "").trim();
  if (!t || /<[a-z][\s\S]*>/i.test(t)) return "";
  return t;
}

export function inputToBp(raw: string, _decimalComma: boolean): number {
  const t = raw.trim().replace(/\u00a0/g, "").replace(/\s/g, "").replace(/−/g, "-").replace(/%/g, "");
  if (t === "") return 0;
  const normalized = normalizeDecimal(t);
  const n = Number(normalized);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(10000, Math.round(n * 100));
}

function storedDiscount(bp: number, cents: number, decimalComma: boolean): { discount: string; discount_kind: "pct" | "eur" } {
  if (cents > 0 && bp <= 0) {
    return { discount: centsToInput(cents, decimalComma), discount_kind: "eur" };
  }
  return { discount: bpToInput(bp, decimalComma), discount_kind: "pct" };
}

export function bpToInput(bp: number, decimalComma: boolean): string {
  if (!bp) return "";
  const pct = bp / 100;
  if (Number.isInteger(pct)) return String(pct);
  return String(pct).replace(".", decimalComma ? "," : ".");
}

export function quoteValidUntil(q: { valid_until?: string | null; due_date?: string | null; doc_type?: string }): string {
  if (q.doc_type && q.doc_type !== "quote") return q.due_date || "";
  return q.valid_until || q.due_date || "";
}

export { inputToCents, centsToInput, todayIso, addDays };
